import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { MessageCircle, Home, Users, Building2, Smartphone, CreditCard, ShoppingCart, Car } from 'lucide-react';
import SEO from '@/components/SEO';
import DigiMitraBookingForm from '@/components/DigiMitraBookingForm';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Services = () => {
  const [showDigiMitraModal, setShowDigiMitraModal] = useState(false);

  const aiFeatures = [
    { icon: <Smartphone />, title: 'Order Food', desc: 'Coming Soon' },
    { icon: <ShoppingCart />, title: 'Order Groceries', desc: 'Coming Soon' },
    { icon: <Car />, title: 'Book Cab', desc: 'Coming Soon' },
    { icon: <CreditCard />, title: 'Pay Bills', desc: 'Coming Soon' },
    { icon: <Building2 />, title: 'Order Medicine', desc: 'Coming Soon' },
    { icon: <Users />, title: 'Book Services', desc: 'Coming Soon' }
  ];

  return (
    <div className="page-container">
      <SEO 
        title="Elderra Services - Voice Companion on WhatsApp, DigiMitra Home Visits & Training"
        description="Get personalized digital training for seniors. Elderra Voice Companion on WhatsApp for daily tasks, DigiMitra home visits at ₹499/hour, community meetups, and government schemes access."
        keywords="DigiMitra home visits, Elderra Voice Companion WhatsApp seniors, digital training elderly, senior tech support India, elderly app training"
        ogUrl="https://foundation-hub-33.preview.emergentagent.com/services"
      />
      {/* Hero Section */}
      <section className="page-hero">
        <div className="container">
          <h1 className="page-title" data-testid="services-title">Our Services</h1>
          <p className="page-subtitle">
            Comprehensive digital solutions for India's senior citizens
          </p>
        </div>
      </section>

      {/* Elderra Voice Companion on WhatsApp */}
      <section className="service-detail-section">
        <div className="container">
          <div className="service-detail-content">
            <div className="service-detail-text">
              <MessageCircle className="service-detail-icon" size={64} />
              <h2>Elderra Voice Companion</h2>
              <p className="service-detail-subtitle">
                Talk to Elderra like you're speaking to your grandchildren. No app downloads, no complex interfaces.
              </p>
              <p>
                Our AI-powered Voice Companion on WhatsApp aggregates all essential apps and services into one simple conversation. Just send a message or a voice note, and Elderra handles the rest.
              </p>
              <div className="service-features">
                <h3>What You Can Do:</h3>
                <div className="features-grid">
                  {aiFeatures.map((feature, index) => (
                    <div key={index} className="feature-item">
                      <div className="feature-item-icon">{feature.icon}</div>
                      <div>
                        <strong>{feature.title}</strong>
                        <p>{feature.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="service-highlight">
                <p><strong>Multilingual Support:</strong> Communicate in your preferred language</p>
                <p><strong>AI-Powered:</strong> Just take a photo of your prescription or speak naturally</p>
                <p><strong>24/7 Available:</strong> Get help anytime, anywhere</p>
              </div>
              <a
                href="https://wa.me/919205070300?text=Hi%20Elderra%2C%20I%27d%20like%20to%20try%20the%20Voice%20Companion"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="services-voice-companion-cta"
              >
                <Button className="btn-primary" style={{ marginTop: '1.5rem' }}>Chat on WhatsApp</Button>
              </a>
            </div>
            <div className="service-detail-image">
              <img 
                src="https://customer-assets.emergentagent.com/job_digital-seniors-3/artifacts/yik6wjge_Screen%20Shot%202025-09-01%20at%209.55.39%20AM.png" 
                alt="Seniors using Elderra Voice Companion"
              />
            </div>
          </div>
        </div>
      </section>

      {/* DigiMitra Home Visits */}
      <section className="digimitra-section">
        <div className="container">
          <div className="digimitra-content">
            <div className="digimitra-header">
              <Home className="digimitra-main-icon" size={72} />
              <h2>DigiMitra Home Visits</h2>
              <p className="digimitra-price">₹499 per hour</p>
              <p className="digimitra-tagline">
                Your verified, secure, friendly tech support system in your neighborhood
              </p>
            </div>

            <div className="digimitra-services-grid">
              <div className="digimitra-service">
                <h3>Onboarding</h3>
                <p>Get started with Elderra membership, setup WhatsApp assistant, and learn the basics.</p>
              </div>
              <div className="digimitra-service">
                <h3>Troubleshooting</h3>
                <p>Fix technical issues, restore passwords, resolve app problems, and more.</p>
              </div>
              <div className="digimitra-service">
                <h3>Training</h3>
                <p>Learn how to use apps for food ordering, cab booking, online payments, and daily tasks.</p>
              </div>
              <div className="digimitra-service">
                <h3>Concierge Services</h3>
                <p>Help with booking tickets, passport renewal, government forms, and other digital tasks.</p>
              </div>
            </div>

            <div className="digimitra-benefits">
              <h3>DigiMitra Benefits</h3>
              <ul>
                <li>Easier onboarding to Elderra membership</li>
                <li>Last-mile connectivity for tech tools</li>
                <li>Builds trust with verified local support</li>
                <li>Personalized one-on-one training at home</li>
                <li>Same-day or next-day availability</li>
              </ul>
            </div>

            <div className="digimitra-cta">
              <img 
                src="https://customer-assets.emergentagent.com/job_digital-seniors-3/artifacts/wb5kt71f_Bilal%20Coaching.jpeg" 
                alt="DigiMitra helping seniors"
                className="digimitra-image"
              />
              <Button 
                className="btn-primary-large" 
                size="lg"
                onClick={() => setShowDigiMitraModal(true)}
                data-testid="book-digimitra-service"
              >
                Book DigiMitra Home Visit
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Community Meetups */}
      <section className="meetups-section">
        <div className="container">
          <div className="meetups-content">
            <Users className="service-detail-icon" size={64} />
            <h2>Community Meetups</h2>
            <p className="meetups-subtitle">
              4-hour programs designed to help elders navigate the digital world, stay safe, and meet new people
            </p>

            <div className="meetups-modules">
              <h3>What We Cover:</h3>
              <div className="modules-grid">
                <div className="module-card">
                  <h4>Elderra Learning Manual</h4>
                  <p>Comprehensive guide to using digital services safely and effectively</p>
                </div>
                <div className="module-card">
                  <h4>Online Fraud Prevention</h4>
                  <p>Learn to identify and avoid scams, frauds, and deepfakes</p>
                </div>
                <div className="module-card">
                  <h4>Apps for Everyday Use</h4>
                  <p>Hands-on training for essential apps like banking, food, cabs, and more</p>
                </div>
                <div className="module-card">
                  <h4>Tech Trends & AI</h4>
                  <p>Introduction to new technologies and how they can help you</p>
                </div>
              </div>
            </div>

            <div className="meetups-activities">
              <h3>Regular Activities:</h3>
              <ul>
                <li>Fraud alerts and scam updates</li>
                <li>Weekly online learning webinars</li>
                <li>Regular in-person meetups in your city/neighborhood</li>
                <li>Networking with peers facing similar challenges</li>
                <li>Moderated by trained Elderra staff</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Government Services */}
      <section className="govt-services-section">
        <div className="container">
          <h2 className="section-title-center">Government Schemes on WhatsApp</h2>
          <p className="section-subtitle-center">
            Coming Soon
          </p>
          <div className="govt-services-grid">
            <div className="govt-service-card">
              <h3>Central & State Schemes</h3>
              <p>Easy access to all government welfare schemes</p>
            </div>
            <div className="govt-service-card">
              <h3>Pensions</h3>
              <p>Check status, renewals, and applications</p>
            </div>
            <div className="govt-service-card">
              <h3>Police Helpline</h3>
              <p>Direct connection for emergencies and support</p>
            </div>
            <div className="govt-service-card">
              <h3>Passport Services</h3>
              <p>Renewal applications and status tracking</p>
            </div>
            <div className="govt-service-card">
              <h3>Property Tax</h3>
              <p>View and pay property taxes online</p>
            </div>
            <div className="govt-service-card">
              <h3>More Services</h3>
              <p>Aadhaar, PAN, ration cards, and more</p>
            </div>
          </div>
        </div>
      </section>

      <DigiMitraBookingForm open={showDigiMitraModal} onOpenChange={setShowDigiMitraModal} />
    </div>
  );
};

export default Services;